import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import {
  History, Search, ChevronLeft, ChevronRight,
  Loader2, Filter, X, TrendingUp, TrendingDown, Minus, Database, RefreshCw, CheckCircle, AlertCircle
} from 'lucide-react'
import { useDebounce } from '@/hooks/useDebounce'

interface PriceHistoryItem {
  id: number
  reference: string
  family: string | null
  supplier: string | null
  old_cost: number | null
  new_cost: number
  change_percent: number | null
  old_pvp: number | null
  new_pvp: number | null
  old_pvd: number | null
  new_pvd: number | null
  old_vip: number | null
  new_vip: number | null
  detected_at: string
  recalculated: boolean
  recalculated_at: string | null
}

interface PriceHistoryResponse {
  history: PriceHistoryItem[]
  total: number
  page: number
  page_size: number
  total_pages: number
}

interface MonitorStats {
  total_products_tracked: number
  products_with_changes: number
  pending_recalculations: number
}

interface RecalculationProgress {
  current: number
  total: number
  status: 'idle' | 'running' | 'completed' | 'error'
  message: string
  updated: number
  skipped: number
  errors: number
  prestashop_updated?: number
}

export default function PriceHistoryPage() {
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const [isRecalculating, setIsRecalculating] = useState(false)

  // Filtros
  const [filterReference, setFilterReference] = useState('')
  const [filterFamily, setFilterFamily] = useState('')

  // Debounce de filtros
  const debouncedReference = useDebounce(filterReference, 300)
  const debouncedFamily = useDebounce(filterFamily, 300)

  // Reset page when filters change
  useEffect(() => {
    setPage(1)
  }, [debouncedReference, debouncedFamily])

  // Query para obtener historial
  const historyQuery = useQuery({
    queryKey: ['price-history', page, pageSize, debouncedReference, debouncedFamily],
    queryFn: async () => {
      const params = new URLSearchParams()
      params.append('page', String(page))
      params.append('page_size', String(pageSize))
      if (debouncedReference) params.append('reference', debouncedReference)
      if (debouncedFamily) params.append('family', debouncedFamily)

      const res = await axios.get(`/api/pricing/cost-monitor/history?${params.toString()}`)
      return res.data as PriceHistoryResponse
    }
  })

  const history = historyQuery.data?.history || []
  const total = historyQuery.data?.total || 0
  const totalPages = historyQuery.data?.total_pages || 1

  // Query para obtener estadísticas del monitor
  const statsQuery = useQuery({
    queryKey: ['price-monitor-stats'],
    queryFn: async () => {
      const res = await axios.get('/api/pricing/cost-monitor/stats')
      return res.data as MonitorStats
    }
  })

  const productsTracked = statsQuery.data?.total_products_tracked || 0
  const pendingRecalculations = statsQuery.data?.pending_recalculations || 0

  // Query para obtener progreso del recálculo (polling mientras está en ejecución)
  const progressQuery = useQuery({
    queryKey: ['recalculation-progress'],
    queryFn: async () => {
      const res = await axios.get('/api/pricing/batch-calculate/status')
      return res.data as RecalculationProgress
    },
    enabled: isRecalculating,
    refetchInterval: isRecalculating ? 1000 : false
  })

  // Detener polling cuando termine
  useEffect(() => {
    if (progressQuery.data?.status === 'completed' || progressQuery.data?.status === 'error') {
      setIsRecalculating(false)
      // Refrescar datos
      queryClient.invalidateQueries({ queryKey: ['price-history'] })
      queryClient.invalidateQueries({ queryKey: ['price-monitor-stats'] })
    }
  }, [progressQuery.data?.status, queryClient])

  // Mutación para iniciar recálculo
  const recalculateMutation = useMutation({
    mutationFn: async () => {
      const res = await axios.post('/api/pricing/cost-monitor/recalculate-pending')
      return res.data
    },
    onSuccess: () => {
      setIsRecalculating(true)
    }
  })

  const handleRecalculate = () => {
    if (!isRecalculating && pendingRecalculations > 0) {
      recalculateMutation.mutate()
    }
  }

  // Limpiar todos los filtros
  const clearFilters = () => {
    setFilterReference('')
    setFilterFamily('')
  }

  const hasActiveFilters = filterReference || filterFamily

  // Formatear precio
  const formatPrice = (price: number | null) => {
    if (price === null || price === undefined) return '-'
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(price)
  }

  // Formatear porcentaje
  const formatPercent = (percent: number | null) => {
    if (percent === null || percent === undefined) return '-'
    const sign = percent >= 0 ? '+' : ''
    return `${sign}${percent.toFixed(1)}%`
  }

  // Formatear fecha
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // Icono de tendencia
  const TrendIcon = ({ value }: { value: number | null }) => {
    if (value === null || value === undefined || value === 0) {
      return <Minus className="w-4 h-4 text-slate-400" />
    }
    if (value > 0) {
      return <TrendingUp className="w-4 h-4 text-danger-500" />
    }
    return <TrendingDown className="w-4 h-4 text-success-500" />
  }

  return (
    <div className="max-w-[1600px] mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="p-2 bg-amber-100 dark:bg-amber-900 rounded-lg">
            <History className="w-8 h-8 text-amber-600 dark:text-amber-400" />
          </span>
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Historial de Cambios de Precios</h1>
            <p className="text-slate-600 dark:text-slate-400">
              {total.toLocaleString('es-ES')} cambios registrados
            </p>
          </div>
        </div>

        {/* Indicador de productos monitorizados */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-slate-700 rounded-lg">
            <Database className="w-5 h-5 text-primary-500" />
            <div className="text-sm">
              <span className="font-semibold text-slate-900 dark:text-white">
                {productsTracked.toLocaleString('es-ES')}
              </span>
              <span className="text-slate-500 dark:text-slate-400"> productos monitorizados</span>
            </div>
          </div>

          {/* Mostrar progreso durante recálculo */}
          {isRecalculating && progressQuery.data && (
            <div className="flex items-center gap-3 px-4 py-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
              <Loader2 className="w-4 h-4 animate-spin text-primary-600 dark:text-primary-400" />
              <div className="text-sm">
                <span className="font-medium text-primary-700 dark:text-primary-300">
                  {progressQuery.data.current}/{progressQuery.data.total}
                </span>
                <span className="text-primary-600 dark:text-primary-400 ml-2">
                  {progressQuery.data.message}
                </span>
              </div>
            </div>
          )}

          {/* Mostrar resultado al completar */}
          {!isRecalculating && progressQuery.data?.status === 'completed' && (
            <div className="flex items-center gap-2 px-4 py-2 bg-success-100 dark:bg-success-900/30 rounded-lg">
              <CheckCircle className="w-4 h-4 text-success-600 dark:text-success-400" />
              <span className="text-sm font-medium text-success-700 dark:text-success-400">
                {progressQuery.data.message}
              </span>
            </div>
          )}

          {/* Mostrar error */}
          {!isRecalculating && progressQuery.data?.status === 'error' && (
            <div className="flex items-center gap-2 px-4 py-2 bg-danger-100 dark:bg-danger-900/30 rounded-lg">
              <AlertCircle className="w-4 h-4 text-danger-600 dark:text-danger-400" />
              <span className="text-sm font-medium text-danger-700 dark:text-danger-400">
                Error: {progressQuery.data.message}
              </span>
            </div>
          )}

          {/* Botón de recalcular pendientes */}
          {pendingRecalculations > 0 && !isRecalculating && (
            <button
              onClick={handleRecalculate}
              disabled={recalculateMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-warning-500 hover:bg-warning-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {recalculateMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              <span className="text-sm font-medium">
                Recalcular {pendingRecalculations} pendientes
              </span>
            </button>
          )}
        </div>
      </div>

      {/* Filters Card */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-slate-500" />
            <h2 className="font-semibold text-slate-900 dark:text-white">Filtros</h2>
          </div>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1 text-sm text-slate-600 dark:text-slate-400 hover:text-danger-600 dark:hover:text-danger-400 transition-colors"
            >
              <X className="w-4 h-4" />
              Limpiar filtros
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Filtro por referencia */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Referencia
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={filterReference}
                onChange={(e) => setFilterReference(e.target.value)}
                placeholder="Buscar por referencia..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          {/* Filtro por familia */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Familia
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={filterFamily}
                onChange={(e) => setFilterFamily(e.target.value)}
                placeholder="Código de familia..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          {/* Selector de tamaño de página */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Mostrar
            </label>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value))
                setPage(1)
              }}
              className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value={25}>25 por página</option>
              <option value={50}>50 por página</option>
              <option value={100}>100 por página</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table Card */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
        {historyQuery.isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
            <span className="ml-2 text-slate-600 dark:text-slate-400">Cargando historial...</span>
          </div>
        ) : historyQuery.isError ? (
          <div className="flex items-center justify-center py-12 text-danger-500">
            Error al cargar el historial
          </div>
        ) : history.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-slate-500">
            <History className="w-12 h-12 mb-2 opacity-50" />
            <p>No hay cambios de precio registrados</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 dark:bg-slate-900/50">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                      Referencia
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                      Fam.
                    </th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                      Coste Ant.
                    </th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                      Coste Nuevo
                    </th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                      % Cambio
                    </th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                      PVP Ant.
                    </th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                      PVP Nuevo
                    </th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                      PVD Ant.
                    </th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                      PVD Nuevo
                    </th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                      VIP Ant.
                    </th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                      VIP Nuevo
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                      Fecha
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {history.map((item) => (
                    <tr
                      key={item.id}
                      className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
                    >
                      <td className="px-3 py-3 text-sm font-medium text-slate-900 dark:text-white">
                        {item.reference}
                      </td>
                      <td className="px-3 py-3 text-sm text-slate-600 dark:text-slate-400">
                        {item.family || '-'}
                      </td>
                      <td className="px-3 py-3 text-sm text-right text-slate-600 dark:text-slate-400">
                        {formatPrice(item.old_cost)}
                      </td>
                      <td className="px-3 py-3 text-sm text-right font-medium text-slate-900 dark:text-white">
                        {formatPrice(item.new_cost)}
                      </td>
                      <td className="px-3 py-3 text-sm text-right">
                        <span className={`inline-flex items-center gap-1 ${
                          item.change_percent !== null && item.change_percent > 0
                            ? 'text-danger-600 dark:text-danger-400'
                            : item.change_percent !== null && item.change_percent < 0
                            ? 'text-success-600 dark:text-success-400'
                            : 'text-slate-500'
                        }`}>
                          <TrendIcon value={item.change_percent} />
                          {formatPercent(item.change_percent)}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-sm text-right text-slate-600 dark:text-slate-400">
                        {formatPrice(item.old_pvp)}
                      </td>
                      <td className="px-3 py-3 text-sm text-right font-medium text-slate-900 dark:text-white">
                        {formatPrice(item.new_pvp)}
                      </td>
                      <td className="px-3 py-3 text-sm text-right text-slate-600 dark:text-slate-400">
                        {formatPrice(item.old_pvd)}
                      </td>
                      <td className="px-3 py-3 text-sm text-right font-medium text-slate-900 dark:text-white">
                        {formatPrice(item.new_pvd)}
                      </td>
                      <td className="px-3 py-3 text-sm text-right text-slate-600 dark:text-slate-400">
                        {formatPrice(item.old_vip)}
                      </td>
                      <td className="px-3 py-3 text-sm text-right font-medium text-slate-900 dark:text-white">
                        {formatPrice(item.new_vip)}
                      </td>
                      <td className="px-3 py-3 text-sm text-slate-600 dark:text-slate-400 whitespace-nowrap">
                        {formatDate(item.recalculated_at || item.detected_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 dark:border-slate-700">
              <div className="text-sm text-slate-600 dark:text-slate-400">
                Mostrando {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, total)} de {total.toLocaleString('es-ES')}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Anterior
                </button>
                <span className="text-sm text-slate-600 dark:text-slate-400">
                  Página {page} de {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Siguiente
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
